import { FLAPPY_HEIGHT, FLAPPY_WIDTH, flappyConfig } from "./flappy-config";

export type FlappyState = "READY" | "PLAYING" | "DEAD" | "RESULT";

export interface PlayerState {
  x: number;
  y: number;
  velocityY: number;
}

export interface ObstaclePair {
  x: number;
  gapY: number;
  scored: boolean;
}

function overlaps(aLeft: number, aTop: number, aSize: number, bLeft: number, bTop: number, bWidth: number, bHeight: number): boolean {
  return aLeft < bLeft + bWidth && aLeft + aSize > bLeft && aTop < bTop + bHeight && aTop + aSize > bTop;
}

export class FlappyEngine {
  state: FlappyState = "READY";
  score = 0;
  bestScore: number;
  player: PlayerState = { x: flappyConfig.playerX, y: flappyConfig.playerY, velocityY: 0 };
  obstacles: ObstaclePair[] = [];
  private spawnTimer = 0.25;

  constructor(bestScore = 0) {
    this.bestScore = bestScore;
  }

  reset(): void {
    this.state = "READY";
    this.score = 0;
    this.player = { x: flappyConfig.playerX, y: flappyConfig.playerY, velocityY: 0 };
    this.obstacles = [];
    this.spawnTimer = 0.25;
  }

  start(): void {
    if (this.state === "READY") this.state = "PLAYING";
  }

  flap(): void {
    if (this.state === "READY") this.start();
    if (this.state === "PLAYING") this.player.velocityY = flappyConfig.flapVelocity;
  }

  update(deltaSeconds: number): void {
    if (this.state !== "PLAYING") return;
    const dt = Math.min(Math.max(deltaSeconds, 0), 0.033);
    this.player.velocityY += flappyConfig.gravity * dt;
    this.player.y += this.player.velocityY * dt;
    this.spawnTimer += dt;

    const speed = flappyConfig.obstacleSpeed + Math.min(this.score, 20) * 1.5;
    this.obstacles.forEach((obstacle) => {
      obstacle.x -= speed * dt;
      if (!obstacle.scored && obstacle.x + flappyConfig.obstacleWidth < this.player.x) {
        obstacle.scored = true;
        this.score += 1;
        this.bestScore = Math.max(this.bestScore, this.score);
      }
    });
    this.obstacles = this.obstacles.filter((obstacle) => obstacle.x + flappyConfig.obstacleWidth > -8);

    if (this.spawnTimer >= flappyConfig.spawnInterval) {
      this.spawnTimer -= flappyConfig.spawnInterval;
      this.obstacles.push(this.createObstacle());
    }
    if (this.player.y < -flappyConfig.playerSize || this.player.y + flappyConfig.playerSize > flappyConfig.groundY || this.isColliding()) {
      this.state = "DEAD";
      this.bestScore = Math.max(this.bestScore, this.score);
    }
  }

  markResult(): void {
    if (this.state === "DEAD") this.state = "RESULT";
  }

  private createObstacle(): ObstaclePair {
    const margin = 88;
    const gapY = margin + Math.random() * (flappyConfig.groundY - margin * 2 - flappyConfig.obstacleGap);
    return { x: FLAPPY_WIDTH + 8, gapY, scored: false };
  }

  private isColliding(): boolean {
    const hitboxOffset = (flappyConfig.playerSize - flappyConfig.hitboxSize) / 2;
    const left = this.player.x + hitboxOffset;
    const top = this.player.y + hitboxOffset;
    const size = flappyConfig.hitboxSize;
    return this.obstacles.some((obstacle) => {
      const topHit = overlaps(left, top, size, obstacle.x, 0, flappyConfig.obstacleWidth, obstacle.gapY);
      const bottomTop = obstacle.gapY + flappyConfig.obstacleGap;
      const bottomHit = overlaps(left, top, size, obstacle.x, bottomTop, flappyConfig.obstacleWidth, FLAPPY_HEIGHT - bottomTop);
      return topHit || bottomHit;
    });
  }
}
